import { Button, Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { Fragment, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { IUserDetailsInfo } from "../Profile/interface/IUserDetails";
import { useReplacementSimForm } from "./hook/useReplacementSimForm";
import { useSimCardsRequests } from "./hook/useSimCardsRequests";
import { ReplacementSimErrorModal } from "./ReplacementSimErrorModal";
import { IUserAddress } from "@common/Eircode/interface/IAddressForm";
import { Address } from "@common/Eircode/Address";
import { Loader } from "@common/Loader/Loader";
import Divider from "@mui/material/Divider";

interface IParams {
    closeForm(): void;
    onSuccessCallback(): void;
    deliveryAddress?: IUserAddress;
    userDetails?: IUserDetailsInfo;
}

export const ReplacementSimAddressForm = (props: IParams) => {
    const { closeForm, onSuccessCallback, userDetails, deliveryAddress } = props;
    const { t } = useTranslation(["sim", "common"]);
    const { errorCode } = useSimCardsRequests();

    const [userAddress, setUserAddress] = useState<IUserAddress>();

    const openErrorModal = (address: IUserAddress) => setUserAddress(address);
    const closeErrorModal = () => setUserAddress(undefined);

    const {
        postReplacementSimState,
        barPreviousSim,
        postReplacementSimInitial,
        postReplacementSimAfterError,
        setBarPreviousSim,
        formMethods,
    } = useReplacementSimForm(deliveryAddress, userDetails);

    const handleConfirmReplacementSim = (address: IUserAddress) => () => {
        postReplacementSimAfterError(closeErrorModal, onSuccessCallback, address);
    };

    const handleSubmit = formMethods.handleSubmit((formData: IUserAddress) => {
        if (errorCode) {
            openErrorModal(formData);
        } else {
            postReplacementSimInitial(formData, openErrorModal, onSuccessCallback);
        }
    });

    return (
        <Fragment>
            {userAddress && (
                <ReplacementSimErrorModal
                    close={closeErrorModal}
                    confirm={handleConfirmReplacementSim(userAddress)}
                    errorCode={errorCode}
                    postReplacementSimState={postReplacementSimState}
                />
            )}
            <Typography align="center" color="secondary" gutterBottom variant="h3">
                {t("sim:deliveryAddress")}
            </Typography>
            <Divider light style={{ marginBottom: "20px" }} variant="middle" />
            <form data-testid="order-sim-form" onSubmit={handleSubmit}>
                <Address
                    addressForm={formMethods}
                    buttonColor="secondary"
                    eirCodeLogoColor="blue"
                    isEditing
                    isUpdated={postReplacementSimState.isLoading}
                />
                <Grid className="replacementSim-blockService" container>
                    <Grid item xs={12}>
                        <Typography align="center" color="secondary" gutterBottom variant="h3">
                            {t("sim:blockService")}
                        </Typography>
                    </Grid>
                    <Grid className="replacementSim__divider" item xs={12}>
                        <Divider light variant="middle" />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="center" variant="normal">
                            <Trans i18nKey="blockMyServicesHelper" ns="sim" />
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={barPreviousSim}
                                    color="secondary"
                                    style={{ padding: "10px 4px 15px 12px" }}
                                    onChange={() => setBarPreviousSim(!barPreviousSim)}
                                />
                            }
                            disabled={postReplacementSimState.isLoading}
                            label={t("sim:serviceBlockCheckbox")}
                        />
                    </Grid>
                </Grid>
                <Grid container justifyContent="center" spacing={2}>
                    {postReplacementSimState.isLoading && (
                        <Grid item sm={2} xs={4}>
                            <Loader size={40} />
                        </Grid>
                    )}
                    {!postReplacementSimState.isLoading && (
                        <Fragment>
                            <Grid item>
                                <Button color="secondary" size="large" variant="outlined" onClick={closeForm}>
                                    {t("common:cancel")}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button color="secondary" id="submit" size="large" type="submit" variant="contained">
                                    {t("sim:sendNewSim")}
                                </Button>
                            </Grid>
                        </Fragment>
                    )}
                </Grid>
            </form>
        </Fragment>
    );
};
